import React, { useState, useEffect } from 'react';
import { BriefcaseBusiness, MapPin, Phone, Globe, Plus, ExternalLink, Edit2, Trash2, Tag, Clock, MoreVertical, MessageSquare } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import SearchableSelect from '../../components/common/SearchableSelect';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import styles from './MyBusiness.module.css';

const TimePicker = ({ value, onChange }) => {
  let h = '09', m = '00', ampm = 'AM';
  if (value && value.includes(':')) {
    const parts = value.split(' ');
    const timeParts = parts[0].split(':');
    h = timeParts[0];
    m = timeParts[1] || '00';
    ampm = parts[1] || 'AM';
  }

  const hours = Array.from({length: 12}, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%' }}>
      <select 
        value={h} 
        onChange={(e) => onChange(`${e.target.value}:${m} ${ampm}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
      >
        {hours.map(hr => <option key={hr} value={hr}>{hr}</option>)}
      </select>
      <span style={{ fontWeight: 'bold', color: '#64748b' }}>:</span>
      <select 
        value={m} 
        onChange={(e) => onChange(`${h}:${e.target.value} ${ampm}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
      >
        {!minutes.includes(m) && <option value={m}>{m}</option>}
        {minutes.map(min => <option key={min} value={min}>{min}</option>)}
      </select>
      <select 
        value={ampm} 
        onChange={(e) => onChange(`${h}:${m} ${e.target.value}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#f8fafc', fontSize: '14px', fontWeight: '600', color: '#0f172a', cursor: 'pointer' }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const MyBusiness = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [selectedReviewsBiz, setSelectedReviewsBiz] = useState(null);

  const baseStandardCategories = [
    'Food & Catering', 'Restaurants & Cafes', 'Bakery & Sweets', 'Grocery & General Store',
    'Health & Wellness', 'Doctors & Clinics', 'Pharmacy', 'Fitness & Gym',
    'Education & Coaching', 'Schools & Colleges', 'Coaching & Training', 'Computer & Skill Classes',
    'Fashion & Beauty', 'Clothing & Boutique', 'Jewellery & Accessories', 'Salon & Beauty', 'Makeup Artist',
    'Home & Decor', 'Interior Design', 'Furniture', 'Electrical & Plumbing',
    'Event & Wedding Services', 'Event Planner', 'Wedding Planner', 'Wedding Decorator', 'Catering Services', 'Photographer & Videographer', 'DJ & Music', 'Tent & Event Setup', 'Florist & Decoration',
    'Professional Services', 'CA & Accounting', 'Legal Services', 'Insurance', 'Consulting', 'Digital Marketing',
    'Automotive', 'Car & Bike Dealer', 'Auto Repair & Service', 'Car Wash & Detailing', 'Auto Parts',
    'Real Estate', 'Property Dealer', 'Builders & Developers', 'Rental & Property Services',
    'Technology & IT', 'IT Services', 'Software & Web Development', 'Computer & Mobile', 'Electronics',
    'Retail & Shopping', 'Supermarket', 'Gifts & Stationery', 'Sports & Fitness Store', 'Furniture & Lifestyle',
    'Travel & Tourism', 'Travel Agency', 'Tour Operator', 'Hotels & Resorts', 'Transport & Cab',
    'Construction & Manufacturing', 'Construction', 'Architecture', 'Building Materials', 'Manufacturing',
    'Agriculture & Farming', 'Agriculture Services', 'Seeds & Fertilizers', 'Farm Equipment',
    'Entertainment & Media', 'Music & Entertainment', 'Media & Advertising', 'Printing & Publishing'
  ];
  const [availableCategories, setAvailableCategories] = useState([...baseStandardCategories, 'Other']);

  const [formData, setFormData] = useState({
    name: '',
    category: 'Food & Catering',
    customCategory: '',
    description: '',
    services: '',
    location: '',
    phone: '',
    email: '',
    workingHours: '',
    website: '',
    ownerName: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const fetchMyBusinesses = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/resident/business');
      setBusinesses(data);
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyBusinesses();
    const fetchCategories = async () => {
      try {
        const { data } = await api.get('/public/businesses/categories');
        const customCategories = data.filter(cat => !baseStandardCategories.includes(cat) && cat !== 'Other');
        setAvailableCategories([...baseStandardCategories, ...customCategories, 'Other']);
      } catch (error) {
        setAvailableCategories([...baseStandardCategories, 'Other']);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'customCategory') return; // Handle category specially
        if (key === 'category') {
          submitData.append('category', formData.category === 'Other' ? formData.customCategory : formData.category);
        } else if (formData[key] !== undefined && formData[key] !== null) {
          submitData.append(key, formData[key]);
        }
      });
      if (imageFile) submitData.append('image', imageFile);
      if (logoFile) submitData.append('logo', logoFile);

      if (isEditing) {
        await api.put(`/resident/business/${editId}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Business updated successfully!');
      } else {
        await api.post('/resident/business', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Business registered successfully!');
      }
      fetchMyBusinesses();
      handleCloseDrawer();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = (id) => {
    toast((t) => (
      <div style={{ padding: '8px' }}>
        <p style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '500', color: '#fff' }}>
          Are you sure you want to delete this business?
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button 
            onClick={() => toast.dismiss(t.id)}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontSize: '13px', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await api.delete(`/resident/business/${id}`);
                toast.success('Business deleted');
                fetchMyBusinesses();
              } catch (error) {
                toast.error('Failed to delete business');
              }
            }}
            style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#ef4444', color: '#fff', fontSize: '13px', cursor: 'pointer' }}
          >
            Delete
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      position: 'top-center',
    });
  };

  const openAddDrawer = () => {
    setIsEditing(false);
    setEditId(null);
    setFormData({
      name: '', category: 'Food & Catering', customCategory: '', description: '', services: '', location: '', phone: '', email: '', workingHours: '', website: '', instagram: '', facebook: '', ownerName: ''
    });
    setImageFile(null);
    setLogoFile(null);
    setIsDrawerOpen(true);
  };

  const openEditDrawer = (biz) => {
    setIsEditing(true);
    setEditId(biz._id);
    const isStandardCategory = availableCategories.includes(biz.category) && biz.category !== 'Other';
    setFormData({
      name: biz.name || '',
      category: isStandardCategory ? biz.category : 'Other',
      customCategory: isStandardCategory ? '' : biz.category,
      description: biz.description || '',
      services: biz.services ? biz.services.join(', ') : '',
      location: biz.location || '',
      phone: biz.phone || '',
      email: biz.email || '',
      workingHours: biz.workingHours || '',
      website: biz.website || '',
      instagram: biz.instagram || '',
      facebook: biz.facebook || '',
      ownerName: biz.ownerName || ''
    });
    setImageFile(null);
    setLogoFile(null);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openReviewsModal = (biz) => {
    setSelectedReviewsBiz(biz);
    setReviewsModalOpen(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Resident Directory & Business</h1>
          <p>Discover services offered by your neighbors or list your own</p>
        </div>
        <button className={styles.addBtn} onClick={openAddDrawer}>
          <Plus size={16} /> List Business
        </button>
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', marginTop: '2rem' }}>Loading...</p>
      ) : businesses.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '3rem', color: '#64748b' }}>
          <BriefcaseBusiness size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3>No Businesses Registered</h3>
          <p>You haven't listed any business yet. Click the button above to add one.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {businesses.map(biz => (
            <div key={biz._id} className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardHeader}>
                  <div className={styles.iconWrapper}>
                    <BriefcaseBusiness size={28} />
                  </div>
                  
                  <div className={styles.headerText}>
                    <h3 className={styles.businessTitle}>{biz.name}</h3>
                    <span className={styles.categoryBadge}>
                      <Tag size={14} /> {biz.category}
                    </span>
                  </div>

                  <div className={styles.statusMenuContainer}>
                    <span className={styles.statusBadge} style={{ 
                      backgroundColor: biz.status === 'Approved' ? '#dcfce7' : biz.status === 'Rejected' ? '#fee2e2' : '#fef9c3',
                      color: biz.status === 'Approved' ? '#166534' : biz.status === 'Rejected' ? '#991b1b' : '#854d0e'
                    }}>
                      <Clock size={14} /> {biz.status}
                    </span>
                  </div>
                </div>

                <div className={styles.infoList}>
                  <div className={styles.infoRow}>
                    <div className={`${styles.iconCircle} ${styles.green}`}>
                      <MapPin size={16} />
                    </div>
                    <span>{biz.location || 'No location specified'}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <div className={`${styles.iconCircle} ${styles.blue}`}>
                      <Phone size={16} />
                    </div>
                    <span>{biz.phone || 'No phone provided'}</span>
                  </div>
                  {biz.website && (
                    <div className={styles.infoRow}>
                      <div className={`${styles.iconCircle} ${styles.purple}`}>
                        <Globe size={16} />
                      </div>
                      <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer">
                        {biz.website} <ExternalLink size={12} />
                      </a>
                    </div>
                  )}
                </div>
                
                <div className={styles.cardActions}>
                  <button onClick={() => openReviewsModal(biz)} className={`${styles.btnAction} ${styles.btnReviews}`}>
                    <MessageSquare size={16} /> Reviews ({biz.reviews || 0})
                  </button>
                  <button onClick={() => openEditDrawer(biz)} className={`${styles.btnAction} ${styles.btnEdit}`}>
                    <Edit2 size={16} /> Edit
                  </button>
                  <button onClick={() => handleDelete(biz._id)} className={`${styles.btnAction} ${styles.btnDelete}`}>
                    <Trash2 size={16} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={handleCloseDrawer} 
        title={isEditing ? "Edit Business" : "List Your Business"}
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Business Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Fresh Bakes" />
          </div>
          <div className="formGroup">
            <label>Category</label>
            <SearchableSelect name="category" value={formData.category} onChange={handleChange} id="category-select">
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </SearchableSelect>
          </div>
          {formData.category === 'Other' && (
            <div className="formGroup">
              <label>Custom Category Name</label>
              <input type="text" name="customCategory" value={formData.customCategory} onChange={handleChange} required placeholder="Enter custom category" />
            </div>
          )}
          <div className="formGroup">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} required placeholder="Tell us about your business" rows="3" />
          </div>
          <div className="formGroup">
            <label>Services (comma separated)</label>
            <input name="services" value={formData.services} onChange={handleChange} placeholder="e.g. Delivery, Dine-in" />
          </div>
          <div className="formGroup">
            <label>Location</label>
            <input name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Tower-2, Flat 101" />
          </div>
          <div className="formGroup">
            <label>Phone Number</label>
            <input name="phone" value={formData.phone} onChange={handleChange} required placeholder="Contact number" />
          </div>
          <div className="formGroup">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="business@example.com" />
          </div>
          <div className="formGroup">
            <label>Working Hours</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500', cursor: 'pointer', fontSize: '14px', color: '#475569' }}>
                <input 
                  type="checkbox" 
                  checked={formData.workingHours === '24/7'} 
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, workingHours: '24/7' });
                    } else {
                      setFormData({ ...formData, workingHours: '' });
                    }
                  }} 
                  style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#014d33' }}
                />
                Open 24/7
              </label>
            </div>
            
            {formData.workingHours !== '24/7' && (
              <div className={styles.timePickerContainer}>
                <div className={styles.timePickerBlock}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Start Time</span>
                  <TimePicker
                    value={formData.workingHours?.includes(' - ') ? formData.workingHours.split(' - ')[0] : (formData.workingHours === '24/7' ? '' : formData.workingHours || '09:00 AM')}
                    onChange={(newStart) => {
                      const currentEnd = formData.workingHours?.includes(' - ') ? formData.workingHours.split(' - ')[1] : '06:00 PM';
                      setFormData({ ...formData, workingHours: `${newStart} - ${currentEnd}` });
                    }}
                  />
                </div>
                <span className={styles.timeDivider}>to</span>
                <div className={styles.timePickerBlock}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>End Time</span>
                  <TimePicker
                    value={formData.workingHours?.includes(' - ') ? formData.workingHours.split(' - ')[1] : '06:00 PM'}
                    onChange={(newEnd) => {
                      const currentStart = formData.workingHours?.includes(' - ') ? formData.workingHours.split(' - ')[0] : (formData.workingHours === '24/7' ? '' : formData.workingHours || '09:00 AM');
                      setFormData({ ...formData, workingHours: `${currentStart} - ${newEnd}` });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="formGroup">
            <label>Website Link (Optional)</label>
            <input name="website" value={formData.website} onChange={handleChange} placeholder="e.g. www.mybusiness.com" />
          </div>
          <div className="formGroup">
            <label>Instagram ID / Link (Optional)</label>
            <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="e.g. @mypage or instagram.com/mypage" />
          </div>
          <div className="formGroup">
            <label>Facebook ID / Link (Optional)</label>
            <input name="facebook" value={formData.facebook} onChange={handleChange} placeholder="e.g. facebook.com/mypage" />
          </div>
          <div className="formGroup">
            <label>Business Owner Name</label>
            <input name="ownerName" value={formData.ownerName} onChange={handleChange} placeholder="e.g. Rahul Verma" />
          </div>
          <div className="formGroup">
            <label>Banner Image (Max 5MB)</label>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
            <small style={{ color: '#64748b' }}>Recommended size: 1536x1024 pixels. Leave empty to keep existing banner.</small>
          </div>
          <div className="formGroup">
            <label>Logo Image (Max 5MB)</label>
            <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files[0])} />
            <small style={{ color: '#64748b' }}>Leave empty to keep existing logo.</small>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={handleCloseDrawer}>Cancel</button>
            <button type="submit" className="btnSubmit">{isEditing ? "Update Business" : "List Business"}</button>
          </div>
        </form>
      </Drawer>

      {/* Reviews Modal */}
      <Drawer
        isOpen={reviewsModalOpen}
        onClose={() => setReviewsModalOpen(false)}
        title={`Reviews for ${selectedReviewsBiz?.name}`}
      >
        <div style={{ padding: '16px' }}>
          {selectedReviewsBiz?.reviewList && selectedReviewsBiz.reviewList.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                 <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>{selectedReviewsBiz.rating}</div>
                 <div style={{ color: '#fbbf24' }}>★</div>
                 <div style={{ color: '#64748b', fontSize: '14px', marginLeft: 'auto' }}>{selectedReviewsBiz.reviews} Total Reviews</div>
              </div>
              
              {selectedReviewsBiz.reviewList.map((review, idx) => (
                <div key={idx} style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <strong style={{ color: '#0f172a' }}>{review.reviewerName}</strong>
                    <span style={{ color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                      ★ {review.rating}
                    </span>
                  </div>
                  {review.comment && <p style={{ margin: '0 0 8px 0', color: '#475569', fontSize: '14px', lineHeight: '1.5' }}>{review.comment}</p>}
                  <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                    {`${new Date(review.date).getDate()}/${new Date(review.date).getMonth() + 1}/${new Date(review.date).getFullYear()}`}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <MessageSquare size={48} style={{ opacity: 0.2, margin: '0 auto 16px auto' }} />
              <p>No reviews yet. When public users leave reviews, they will appear here!</p>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default MyBusiness;
